<template>
  <input
    ref="input"
    v-model="modelValue"
    type="text"
    :maxlength="maxlength"
    :placeholder="placeholder"
    @focus="onFocus"
    @blur="onBlur"
    @keydown.enter.stop="($event.target as HTMLInputElement)?.blur()"
    @keydown.escape.stop="onEscape"
  />
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';

/**
 * Defines the standard for how keyboard interaction should work for a text input.
 * We stop the keydown events from propagating to the parent element, so that there
 * aren't several effects happening at once. (for example: closing poppers, saving forms)
 */

const modelValue = defineModel<string>();

const emit = defineEmits<{ focus: []; blur: []; save: [value?: string] }>();
const props = defineProps<{
  placeholder?: string;
  maxlength?: number;
  resetOnEmpty?: boolean;
}>();

const initialValue = ref<string | undefined>(undefined);
const input = ref<HTMLInputElement | null>(null);

function onFocus() {
  initialValue.value = modelValue.value;
  emit('focus');
}

function onBlur() {
  saveChanges();
  emit('blur');
}

async function onEscape(event: KeyboardEvent) {
  modelValue.value = initialValue.value;
  await nextTick();
  (event.target as HTMLInputElement)?.blur();
}

function saveChanges() {
  if (props.resetOnEmpty && !modelValue.value?.length) {
    modelValue.value = initialValue.value;
    return;
  }

  if (initialValue.value !== modelValue.value) {
    emit('save', modelValue.value);
  }
}

defineExpose({
  focus: () => input.value?.focus(),
  blur: () => input.value?.blur(),
});
</script>

<style scoped lang="scss">
@import '@styles/variables';
</style>
